import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import router from "@/router";
import { authError, authLog, authLogToken } from "@/utils/logger";
import { useAuthStore } from "@/stores/auth";

// Web IMAP API request/response types
export interface LoginRequest {
	username: string;
	password: string;
	server: string;
	port: number;
	use_ssl: boolean;
	verify_ssl?: boolean;
	remember_me?: boolean;
}

export interface LoginResponse {
	access_token: string;
	refresh_token: string;
	token_type: string;
	expires_in: number;
	user_id: string;
	imap_jwt: string;
	session_id: string;
	server_info: {
		server: string;
		port: number;
		use_ssl: boolean;
		imap_jwt: string;
	};
}

export interface LogoutRequest {
	logout_all?: boolean;
}

export interface LogoutResponse {
	message: string;
	sessions_terminated: number;
}

export interface RefreshTokenRequest {
	refresh_token: string;
}

export interface RefreshTokenResponse {
	access_token: string;
	token_type: string;
	expires_in: number;
}

export interface UserInfo {
	user_id: string;
	username: string;
	server: string;
	port: number;
	use_ssl: boolean;
	imap_jwt: string;
	session_id: string;
	created_at: string;
	last_accessed: string;
	expires_at: string;
}

export interface EmailListResponse {
	emails: EmailInfo[];
	total_count: number;
	folder_name: string;
}

export interface EmailInfo {
	uid: string;
	subject: string;
	sender: string;
	recipient: string;
	date: string;
	size: number;
	flags: string[];
	is_read: boolean;
	is_flagged: boolean;
}

export interface EmailDetailResponse {
	uid: string;
	subject: string;
	sender: string;
	recipient: string;
	cc: string;
	bcc: string;
	date: string;
	body_text: string;
	body_html: string;
	attachments: AttachmentInfo[];
	headers: Record<string, string>;
}

export interface AttachmentInfo {
	id: string;
	filename: string;
	content_type: string;
	size: number;
	is_inline?: boolean;
}

export interface AttachmentListResponse {
	success: boolean;
	attachments: AttachmentInfo[];
	total_count: number;
}

export interface EmailSearchRequest {
	query?: string;
	folder_name?: string;
	limit?: number;
	offset?: number;
	sort_by?: string;
	sort_order?: string;
}

export interface EmailFlagUpdate {
	seen?: boolean;
	flagged?: boolean;
	answered?: boolean;
	deleted?: boolean;
}

export interface EmailMoveRequest {
	target_folder: string;
}

export interface EmailOperationResponse {
	success: boolean;
	message: string;
	email_id: string;
	folder_name: string;
	timestamp: string;
}

export interface ErrorResponse {
	success: boolean;
	error: string;
	error_code?: string;
	details?: Record<string, any>;
}

export interface FolderListResponse {
	success: boolean;
	folders: FolderInfo[];
	total_count: number;
}

export interface FolderCountResponse {
	success: boolean;
	folder_name: string;
	count: number;
	message: string;
}

export interface FolderInfo {
	name: string;
	delimiter: string;
	attributes: string[];
	message_count: number;
	unread_count: number;
	is_inbox: boolean;
	is_sent: boolean;
	is_drafts: boolean;
	is_trash: boolean;
	is_spam: boolean;
}

// ============================================================================
// User Preferences API Types
// ============================================================================

export interface PasswordChangeRequest {
	current_password: string;
	new_password: string;
	confirm_password: string;
}

export interface PasswordChangeResponse {
	message: string;
	timestamp: string;
	user_id: string;
}

export interface EmailAlias {
	alias: string;
	user_email: string;
	active: boolean;
	created_at: string;
}

export interface AliasListResponse {
	aliases: EmailAlias[];
	count: number;
	user_id: string;
}

export interface AliasCreateRequest {
	alias: string;
}

export interface AliasCreateResponse {
	message: string;
	alias: EmailAlias;
	timestamp: string;
}

export interface AliasDeleteResponse {
	message: string;
	alias: string;
	timestamp: string;
}

export type PreferenceValue = string | number | boolean;

export interface PreferenceResponse {
	preferences: Record<string, PreferenceValue>;
	user_id: string;
	last_updated: string;
}

export interface PreferenceUpdateRequest {
	preferences: Record<string, PreferenceValue>;
}

export interface PreferenceUpdateResponse {
	message: string;
	preferences: Record<string, PreferenceValue>;
	timestamp: string;
}

export interface APIErrorResponse {
	detail: string;
	field?: string;
	code?: string;
	timestamp: string;
	status: number;
}

// Frontend-specific types for authentication (updated for JWT)

// Get base URL dynamically (for test environment support)
const getBaseURL = (): string => {
	// Access environment variables through import.meta.env (Vite's way)
	const env = import.meta.env;
	return env.VITE_API_URL || "";
};

// SECURITY: Verify HTTPS in production
const verifyHttpsInProduction = (): void => {
	const env = import.meta.env;
	const isProd = env.MODE === "production" || env.PROD;
	const baseURL = getBaseURL();

	if (isProd && baseURL && !baseURL.startsWith("https://")) {
		console.error(
			"SECURITY WARNING: Application is running in production mode but API URL is not HTTPS:",
			baseURL,
		);
		console.error(
			"All production traffic MUST use HTTPS to protect user data.",
		);
	}

	// Check if browser location is HTTPS in production
	if (
		isProd &&
		typeof window !== "undefined" &&
		window.location.protocol !== "https:"
	) {
		console.error(
			"SECURITY WARNING: Application is running in production but not served over HTTPS:",
			window.location.href,
		);
	}
};

// Run security check on module load
verifyHttpsInProduction();

// Create axios instance for UI Service (Gateway)
const api: AxiosInstance = axios.create({
	baseURL: getBaseURL(),
	timeout: 50000,
	headers: {
		"Content-Type": "application/json",
	},
});

// Update base URL dynamically (for test environment)
const updateBaseURL = (newBaseURL: string) => {
	api.defaults.baseURL = newBaseURL;
};

// Export for testing
export { updateBaseURL };

// Request interceptor for Bearer token authentication
api.interceptors.request.use(
	(config) => {
		// List of endpoints that should not have automatic auth token injection
		const unauthenticatedEndpoints = [
			"/api/v1/auth/login",
			"/api/v1/auth/register",
			"/api/v1/health",
		];

		// Check if the request URL matches any unauthenticated endpoint
		const isUnauthenticatedEndpoint = unauthenticatedEndpoints.some(
			(endpoint) => config.url?.includes(endpoint),
		);

		authLog(
			`Request interceptor: ${config.method?.toUpperCase()} ${config.url}`,
		);

		// Only add Authorization header if not an unauthenticated endpoint
		if (!isUnauthenticatedEndpoint) {
			const accessToken = localStorage.getItem("access_token");
			if (accessToken) {
				config.headers.Authorization = `Bearer ${accessToken}`;
				authLog(`Authorization header added to ${config.url}`);
				authLogToken("access_token", accessToken);
			} else {
				authError(
					`No access token available for protected endpoint: ${config.url}`,
				);
			}
		} else {
			authLog(
				`Unauthenticated endpoint, skipping Authorization header: ${config.url}`,
			);
		}

		return config;
	},
	(error) => {
		authError("Request interceptor error", error);
		return Promise.reject(error);
	},
);

// Single-flight refresh: concurrent 401s share one in-flight refresh call.
let refreshInFlight: Promise<string> | null = null;

function clearAuthStorage(): void {
	// Clear Pinia store state so isAuthenticated becomes false before navigation.
	// useAuthStore() also removes the same localStorage keys as a side-effect,
	// but the explicit removeItem calls below act as a safety net for environments
	// where the Pinia instance may not be available (e.g. unit-test isolation).
	try {
		useAuthStore().clearAuth();
	} catch {
		// Pinia not installed (e.g. isolated unit tests) — fall back to manual cleanup.
		localStorage.removeItem("access_token");
		localStorage.removeItem("refresh_token");
		localStorage.removeItem("imap_jwt");
		localStorage.removeItem("user_info");
	}
}

async function navigateToLoginBeforePaint(reason: string = "expired"): Promise<void> {
	authLog(`[WebIMAP-401] Starting navigation to login, reason=${reason}`);
	authLog(`[WebIMAP-401] Current location: ${window.location.pathname}`);
	try {
		authLog(`[WebIMAP-401] Router ready: ${!!(router && router.currentRoute)}`);
		if (router && router.currentRoute) {
			authLog("[WebIMAP-401] Using router.push navigation");
			await router.push({ path: "/login", query: { reason } });
			authLog("[WebIMAP-401] Router navigation completed successfully");
			return;
		}
		throw new Error("Router not ready");
	} catch (e) {
		authError("[WebIMAP-401] Router navigation failed, using fallback", e);
		window.location.href = `/login?reason=${encodeURIComponent(reason)}`;
	}
}

export async function performRefresh(): Promise<string> {
	if (refreshInFlight) return refreshInFlight;
	const refreshToken = localStorage.getItem("refresh_token");
	const imapJwt = localStorage.getItem("access_token"); // current access_token IS the IMAP JWT
	if (!refreshToken || !imapJwt) {
		return Promise.reject(new Error("Missing refresh_token or imap_jwt in storage"));
	}
	refreshInFlight = authAPI
		.refreshToken(refreshToken, imapJwt)
		.then((data) => {
			localStorage.setItem("access_token", data.access_token);
			// imap_jwt and access_token are the same artefact — keep both keys in sync
			localStorage.setItem("imap_jwt", data.access_token);
			authLogToken("access_token (refreshed)", data.access_token);
			return data.access_token;
		})
		.finally(() => {
			refreshInFlight = null;
		});
	return refreshInFlight;
}

// Response interceptor for error handling
api.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error) => {
		const cfg = error.config;
		const status = error.response?.status;
		const errorCode = error.response?.data?.error_code;

		if (status === 401) {
			const refreshable = errorCode !== "INVALID_TOKEN" && !cfg?.__authRetried;
			// Also: never try to refresh a refresh call itself
			const isRefreshCall = cfg?.url?.includes("/api/v1/auth/refresh");

			if (refreshable && !isRefreshCall) {
				try {
					authLog(`[WebIMAP-401] Attempting token refresh for ${cfg?.url}`);
					const newToken = await performRefresh();
					cfg.__authRetried = true;
					cfg.headers = cfg.headers || {};
					cfg.headers.Authorization = `Bearer ${newToken}`;
					authLog(`[WebIMAP-401] Refresh succeeded, retrying ${cfg?.url}`);
					return api(cfg);
				} catch {
					// fall through to hard logout
					authError("[WebIMAP-401] Refresh failed, proceeding to hard logout");
				}
			}

			// Hard logout path: clear storage, navigate to login BEFORE rejecting upstream.
			authError("[WebIMAP-401] Hard logout triggered", {
				url: cfg?.url,
				status,
				errorCode,
				currentPath: window.location.pathname,
				timestamp: new Date().toISOString(),
			});
			clearAuthStorage();
			await navigateToLoginBeforePaint("expired");
		}
		return Promise.reject(error);
	},
);

// Authentication API methods (UI Service Gateway)
export const authAPI = {
	// Login through UI Service
	async login(credentials: LoginRequest): Promise<LoginResponse> {
		authLog("Calling login API...", {
			username: credentials.username,
			server: credentials.server,
		});

		const response = await api.post<LoginResponse>(
			"/api/v1/auth/login",
			credentials,
		);

		authLog("Login API response received", {
			user_id: response.data.user_id,
			session_id: response.data.session_id,
			expires_in: response.data.expires_in,
		});

		// Store tokens and IMAP JWT
		authLog("Storing tokens in localStorage...");
		localStorage.setItem("access_token", response.data.access_token);
		authLogToken("access_token", response.data.access_token);

		localStorage.setItem("refresh_token", response.data.refresh_token);
		authLogToken("refresh_token", response.data.refresh_token);

		localStorage.setItem("imap_jwt", response.data.imap_jwt);
		authLogToken("imap_jwt", response.data.imap_jwt);

		localStorage.setItem(
			"user_info",
			JSON.stringify({
				user_id: response.data.user_id,
				server_info: response.data.server_info,
			}),
		);
		authLog("Tokens and user info stored successfully");

		return response.data;
	},

	// Logout through UI Service
	async logout(logoutData: LogoutRequest = {}): Promise<LogoutResponse> {
		const response = await api.post<LogoutResponse>(
			"/api/v1/auth/logout",
			logoutData,
		);
		// Clear all stored tokens
		localStorage.removeItem("access_token");
		localStorage.removeItem("refresh_token");
		localStorage.removeItem("imap_jwt");
		localStorage.removeItem("user_info");
		return response.data;
	},

	// Refresh token through UI Service
	async refreshToken(refreshToken: string, imapJwt: string): Promise<{ access_token: string; token_type: string; expires_in: number }> {
		const response = await api.post<{ access_token: string; token_type: string; expires_in: number }>(
			"/api/v1/auth/refresh",
			{ refresh_token: refreshToken, imap_jwt: imapJwt },
		);
		return response.data;
	},

	// Get current user info through UI Service
	async getCurrentUser(): Promise<UserInfo> {
		const response = await api.get<UserInfo>("/api/v1/auth/me");
		return response.data;
	},

	// Validate session through UI Service
	async validateSession(): Promise<{ valid: boolean; user?: UserInfo }> {
		const response = await api.get<{ valid: boolean; user?: UserInfo }>(
			"/api/v1/auth/validate",
		);
		return response.data;
	},
};

// Email API methods (UI Service Gateway)
export const emailAPI = {
	// List emails in a folder
	async listEmails(
		folderName: string = "INBOX",
		limit: number = 50,
		offset: number = 0,
		sortBy: string = "date",
		sortOrder: string = "desc",
	): Promise<EmailListResponse> {
		const response = await api.get<EmailListResponse>(
			`/api/v1/folders/${folderName}/emails`,
			{
				params: { limit, offset, sort_by: sortBy, sort_order: sortOrder },
			},
		);
		return response.data;
	},

	// Get email details
	async getEmail(
		folderName: string,
		emailId: string,
	): Promise<EmailDetailResponse> {
		const response = await api.get<{
			email: EmailDetailResponse;
			success: boolean;
		}>(`/api/v1/folders/${folderName}/emails/${emailId}`);
		return response.data.email;
	},

	// Search emails
	async searchEmails(
		folderName: string,
		searchRequest: EmailSearchRequest,
	): Promise<EmailListResponse> {
		// UI Service search endpoint includes folder_name in request body
		const requestWithFolder = {
			...searchRequest,
			folder_name: folderName,
		};
		const response = await api.post<EmailListResponse>(
			"/api/v1/emails/search",
			requestWithFolder,
		);
		return response.data;
	},

	// Update email flags (mark as read/unread, etc.)
	async updateEmailFlags(
		folderName: string,
		emailId: string,
		flags: EmailFlagUpdate,
	): Promise<EmailOperationResponse> {
		const response = await api.patch<EmailOperationResponse>(
			`/api/v1/folders/${folderName}/emails/${emailId}`,
			flags,
		);
		return response.data;
	},

	// Delete email
	async deleteEmail(
		folderName: string,
		emailId: string,
	): Promise<EmailOperationResponse> {
		const response = await api.delete<EmailOperationResponse>(
			`/api/v1/folders/${folderName}/emails/${emailId}`,
		);
		return response.data;
	},

	// Move email to another folder
	async moveEmail(
		folderName: string,
		emailId: string,
		targetFolder: string,
	): Promise<EmailOperationResponse> {
		const response = await api.post<EmailOperationResponse>(
			`/api/v1/folders/${folderName}/emails/${emailId}/move`,
			{
				target_folder: targetFolder,
			},
		);
		return response.data;
	},
};

// Folder API methods (UI Service Gateway)
export const folderAPI = {
	// List all folders
	async listFolders(
		includeAttributes: boolean = true,
		includeCounts: boolean = true,
	): Promise<FolderListResponse> {
		const response = await api.get<FolderListResponse>("/api/v1/folders/", {
			params: {
				include_attributes: includeAttributes,
				include_counts: includeCounts,
			},
		});
		return response.data;
	},

	// Get folder count
	async getFolderCount(folderName: string): Promise<FolderCountResponse> {
		const response = await api.get<FolderCountResponse>(
			`/api/v1/folders/${folderName}/count`,
		);
		return response.data;
	},

	// Get folder unread count
	async getFolderUnreadCount(folderName: string): Promise<FolderCountResponse> {
		const response = await api.get<FolderCountResponse>(
			`/api/v1/folders/${folderName}/unread-count`,
		);
		return response.data;
	},
};

// Attachment API methods (UI Service Gateway)
export const attachmentAPI = {
	// List attachments in an email
	async listAttachments(
		folderName: string,
		emailId: string,
	): Promise<AttachmentListResponse> {
		const response = await api.get<AttachmentListResponse>(
			`/api/v1/folders/${encodeURIComponent(folderName)}/emails/${encodeURIComponent(emailId)}/attachments`,
		);
		return response.data;
	},

	// Download attachment bytes (JWT stays in the Authorization header, never in the URL)
	async downloadAttachment(
		folderName: string,
		emailId: string,
		attachmentId: string,
	): Promise<Blob> {
		const response = await api.get<Blob>(
			`/api/v1/folders/${encodeURIComponent(folderName)}/emails/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(attachmentId)}`,
			{ responseType: 'blob' },
		);
		return response.data;
	},
};

// Health check
export const healthAPI = {
	async check(): Promise<{ status: string; timestamp: string }> {
		const response = await api.get("/api/v1/health");
		return response.data;
	},
};

// User Preferences API methods (UI Service Gateway)
export const preferencesAPI = {
	// Change password
	async changePassword(
		data: PasswordChangeRequest,
	): Promise<PasswordChangeResponse> {
		const response = await api.post<PasswordChangeResponse>(
			"/api/v1/auth/change-password",
			data,
		);
		return response.data;
	},

	// List email aliases
	async listAliases(userId: string): Promise<AliasListResponse> {
		const response = await api.get<AliasListResponse>(
			`/api/v1/users/${userId}/aliases`,
		);
		return response.data;
	},

	// Add email alias
	async addAlias(userId: string, alias: string): Promise<AliasCreateResponse> {
		const response = await api.post<AliasCreateResponse>(
			`/api/v1/users/${userId}/aliases`,
			{ alias },
		);
		return response.data;
	},

	// Delete email alias
	async deleteAlias(
		userId: string,
		alias: string,
	): Promise<AliasDeleteResponse> {
		const response = await api.delete<AliasDeleteResponse>(
			`/api/v1/users/${userId}/aliases/${encodeURIComponent(alias)}`,
		);
		return response.data;
	},

	// Get user preferences
	async getPreferences(userId: string): Promise<PreferenceResponse> {
		const response = await api.get<PreferenceResponse>(
			`/api/v1/users/${userId}/preferences`,
		);
		return response.data;
	},

	// Update user preferences
	async updatePreferences(
		userId: string,
		preferences: Record<string, PreferenceValue>,
	): Promise<PreferenceUpdateResponse> {
		const response = await api.patch<PreferenceUpdateResponse>(
			`/api/v1/users/${userId}/preferences`,
			{ preferences },
		);
		return response.data;
	},
};

export default api;
